import { AssessmentCriteriaField } from '@/components/evaluation/AssessmentItemForm';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import {
  createAssessmentCriteria,
  deleteAssessmentCriteria,
  updateAssessmentCriteria,
} from '@/data/services/assessmentCriteriaServices';
import { Assessment, AssessmentCriteria } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { adaptCriteriaFormToDTO } from '@/utils/evaluation/adaptCriteriaFormToDTO';

export type SaveCriteriaResult = {
  success: { index: number; saved: AssessmentCriteria }[];
  failed: { order: number; reason: unknown }[];
  nextCriteria: AssessmentCriteria[];
};

type SaveCriteriaParams = {
  itemId: number;
  existing: AssessmentCriteria[];
  next: AssessmentCriteriaField[];
};

type UseAssessmentCriteriaProps = {
  evaluation: Evaluation;
  assessment: Assessment;
  cacheManager: ListCacheManager<Evaluation>;
};

type WriteOp = {
  kind: 'create' | 'update';
  index: number;
  order: number;
  promise: Promise<AssessmentCriteria>;
};

type DeleteOp = {
  kind: 'delete';
  order: number;
  id: number;
  promise: Promise<void>;
};

type SaveOp = WriteOp | DeleteOp;

const useAssessmentCriteria = ({
  evaluation,
  assessment,
  cacheManager,
}: UseAssessmentCriteriaProps) => {
  const updateItemCriteriaInCache = (
    itemId: number,
    nextCriteria: AssessmentCriteria[],
  ) => {
    const currentEvaluation = cacheManager.getById(evaluation.id);
    if (!currentEvaluation) return;

    const assessments = currentEvaluation.assessments ?? [];
    const targetAssessment = assessments.find(a => a.id === assessment.id);
    if (!targetAssessment) return;

    const items = targetAssessment.items ?? [];
    if (!items.some(it => it.id === itemId)) return;

    const updatedItems = items.map(it =>
      it.id === itemId ? { ...it, criteria: nextCriteria } : it,
    );
    const updatedAssessments = assessments.map(a =>
      a.id === assessment.id ? { ...a, items: updatedItems } : a,
    );

    cacheManager.update({
      id: currentEvaluation.id,
      assessments: updatedAssessments,
    });
  };

  const createOp = (
    itemId: number,
    index: number,
    payload: Partial<AssessmentCriteria>,
  ): WriteOp => ({
    kind: 'create',
    index,
    order: index + 1,
    promise: createAssessmentCriteria(
      evaluation.id,
      assessment.id,
      itemId,
      payload,
    ),
  });

  const updateOp = (
    itemId: number,
    criterionId: number,
    index: number,
    changes: Partial<AssessmentCriteria>,
  ): WriteOp => ({
    kind: 'update',
    index,
    order: index + 1,
    promise: updateAssessmentCriteria(
      evaluation.id,
      assessment.id,
      itemId,
      criterionId,
      changes,
    ),
  });

  const deleteOp = (
    itemId: number,
    criterion: AssessmentCriteria,
  ): DeleteOp => ({
    kind: 'delete',
    order: criterion.order,
    id: criterion.id,
    promise: deleteAssessmentCriteria(
      evaluation.id,
      assessment.id,
      itemId,
      criterion.id,
    ),
  });

  const buildOps = (
    itemId: number,
    existing: AssessmentCriteria[],
    next: AssessmentCriteriaField[],
  ): SaveOp[] => {
    const existingById = new Map(existing.map(e => [e.id, e]));
    const nextIds = new Set(next.map(c => c.id).filter(Boolean));
    const ops: SaveOp[] = [];

    next.forEach((criterion, index) => {
      const payload = adaptCriteriaFormToDTO(
        criterion as Partial<AssessmentCriteria>,
        index,
      );

      if (!criterion.id) {
        ops.push(createOp(itemId, index, payload));
        return;
      }

      const original = existingById.get(criterion.id);
      const normalizedOriginal =
        original && adaptCriteriaFormToDTO(original, index);
      const changes = normalizedOriginal
        ? buildChangedObject(normalizedOriginal, payload)
        : payload;
      if (Object.keys(changes).length === 0) return;

      ops.push(updateOp(itemId, criterion.id, index, changes));
    });

    existing
      .filter(e => !nextIds.has(e.id))
      .forEach(e => ops.push(deleteOp(itemId, e)));

    return ops;
  };

  const buildCriteriaResult = (
    ops: SaveOp[],
    results: PromiseSettledResult<AssessmentCriteria | void>[],
    existing: AssessmentCriteria[],
  ): SaveCriteriaResult => {
    const success: SaveCriteriaResult['success'] = [];
    const failed: SaveCriteriaResult['failed'] = [];
    const createdItems: AssessmentCriteria[] = [];
    const updatedById = new Map<number, AssessmentCriteria>();
    const deletedIds = new Set<number>();

    results.forEach((result, i) => {
      const op = ops[i];
      if (result.status === 'rejected') {
        failed.push({ order: op.order, reason: result.reason });
        return;
      }
      if (op.kind === 'delete') {
        deletedIds.add(op.id);
        return;
      }
      const saved = result.value as AssessmentCriteria;
      success.push({ index: op.index, saved });
      if (op.kind === 'create') {
        createdItems.push(saved);
      } else {
        updatedById.set(saved.id, saved);
      }
    });

    const nextCriteria = [
      ...existing
        .filter(c => !deletedIds.has(c.id))
        .map(c => updatedById.get(c.id) ?? c),
      ...createdItems,
    ];

    return { success, failed, nextCriteria };
  };

  const saveCriteria = async ({
    itemId,
    existing,
    next,
  }: SaveCriteriaParams): Promise<SaveCriteriaResult> => {
    const ops = buildOps(itemId, existing, next);
    const results = await Promise.allSettled(ops.map(op => op.promise));
    const result = buildCriteriaResult(ops, results, existing);

    updateItemCriteriaInCache(itemId, result.nextCriteria);

    return result;
  };

  return { saveCriteria };
};

export default useAssessmentCriteria;
